import React, { FormEvent, useState } from 'react';
import styled from 'styled-components';
import { Typography, Button, Grid } from '@mui/material';
import { FormContainer } from 'components/FormContainer/FormContainer.styled';
import { FormFieldInterface, FormInterface } from 'components/Form/Form.types';
import FormField from 'components/Form/FormField';
import LoadingElement from 'components/LoadingElement/LoadingElement';
import ErrorText from 'components/ErrorText/ErrorText';

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
`;

const Form = ({
  title,
  fields,
  submitButton,
  onSubmit,
  submitting,
  disabled,
  buttonText,
  index,
  headerActions,
  disclaimer,
}: FormInterface): JSX.Element => {
  const [fieldValues, setFieldValues] = useState(
    fields.map((field, i) => ({
      key: i,
      valid: field.requiredMinimumLength
        ? (String(field.value ?? '').length ?? 0) >= field.requiredMinimumLength
        : true,
    }))
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit && onSubmit();
  };
  const isFormDisabled = disabled || !!fieldValues.find((fieldValue) => !fieldValue.valid);

  const handleChange = (value: string, field: FormFieldInterface, key?: number) => {
    if (!field.validator || field.validator(value)) {
      field.onChange && field.onChange(value, index ?? null);
    }

    if (field.requiredMinimumLength) {
      const fieldValuesCopy = [...fieldValues];
      const valid = value.length >= field.requiredMinimumLength;

      fieldValuesCopy[key!] = { ...fieldValues[key!], valid };
      setFieldValues(fieldValuesCopy);
    }
  };

  return (
    <FormContainer>
      <FormHeader>
        <div />
        {title && (
          <Typography variant='h5' paddingBottom={1}>
            {title}
          </Typography>
        )}
        <div>{headerActions}</div>
      </FormHeader>
      <form onSubmit={(e) => onSubmit && handleSubmit(e)}>
        <Grid container paddingBottom={2} spacing={1} mt={1}>
          {fields.map(
            (field, i) =>
              !field.hide && (
                <Grid
                  xs={field.xs ?? 12}
                  sm={field.sm}
                  md={field.md}
                  lg={field.lg}
                  xl={field.lg}
                  item
                  key={`${field.label}-${field.type}-${i}`}
                >
                  <FormField
                    field={field}
                    fieldIndex={i}
                    onChange={handleChange}
                    marginTop='1rem'
                  />
                </Grid>
              )
          )}
        </Grid>
        {onSubmit &&
          (submitButton ?? (
            <LoadingElement
              loading={submitting}
              element={
                <Button type='submit' disabled={isFormDisabled}>
                  {buttonText ?? 'Submit'}
                </Button>
              }
            />
          ))}
      </form>
      {disclaimer && (
        <ErrorText color='theme.colors.error900' sx={{ textAlign: 'left', marginTop: '1rem' }}>
          {disclaimer}
        </ErrorText>
      )}
    </FormContainer>
  );
};

export default Form;
